import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MessageSquare, Star, Filter, ExternalLink, MapPin, Globe2, Smile, Meh, Frown, ArrowUpDown, RefreshCw } from "lucide-react";
import api from "../../lib/api";
import { Label, EmptyState } from "../../components/common";

/** Central "What customers are saying" feed.
 *
 *  Aggregates:
 *    - Google reviews cached on every linked location (API cap: 5 per store)
 *    - In-app feedback with comments across every accessible location
 *
 *  Filters: source (all / google / in-app), star rating (1-5 chip + "any"),
 *  sort (newest / oldest / lowest / highest).
 */
export default function Reviews() {
  const [sp, setSp] = useSearchParams();
  const initialSource = sp.get("source") || "all";
  const initialStar = sp.get("star") ? Number(sp.get("star")) : null;
  const wantsRefresh = sp.get("refresh") === "1";

  const [source, setSource] = useState(initialSource);
  const [star, setStar] = useState(initialStar);
  const [sort, setSort] = useState("newest");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Keep URL in sync so the view is sharable + bookmarkable.
  useEffect(() => {
    const next = new URLSearchParams();
    if (source !== "all") next.set("source", source);
    if (star != null) next.set("star", String(star));
    setSp(next, { replace: true });
  }, [source, star, setSp]);

  // Force a live Google refresh when the user clicks through from a tile.
  useEffect(() => {
    if (!wantsRefresh) return;
    setRefreshing(true);
    api.post("/admin/locations/benchmarks/refresh-all")
      .catch(() => {})
      .finally(() => {
        setRefreshing(false);
        // Trigger a re-fetch by nudging sort state (same value — effect re-runs).
        setSort((s) => s);
      });
    // Only run once; the param is kept until the user navigates away.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (refreshing) return; // wait for the live refresh to finish first
    setLoading(true);
    const params = new URLSearchParams({ source, sort });
    if (star != null) params.set("star", String(star));
    api.get(`/admin/reviews?${params.toString()}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [source, sort, star, refreshing]);

  return (
    <div className="space-y-6 pb-10">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Label>Voice of Customer</Label>
          <h1 className="font-head text-2xl font-bold text-slate-900 sm:text-3xl">Reviews &amp; Feedback</h1>
          <p className="mt-1 text-sm text-slate-500">
            Everything your customers are saying — Google reviews across linked stores and every in-app comment, in one feed.
          </p>
        </div>
        <button
          type="button"
          onClick={async () => {
            setRefreshing(true);
            try { await api.post("/admin/locations/benchmarks/refresh-all"); }
            catch (e) {}
            setRefreshing(false);
            setSort((s) => s);
          }}
          disabled={refreshing}
          className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-indigo-300 hover:text-indigo-700 disabled:opacity-60"
          data-testid="reviews-refresh"
        >
          <RefreshCw className={`h-3 w-3 ${refreshing ? "animate-spin" : ""}`} />
          {refreshing ? "Fetching latest from Google…" : "Refresh from Google"}
        </button>
      </div>

      {/* Summary strip */}
      <SummaryStrip data={data} loading={loading} />

      {/* Filters */}
      <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-4" data-testid="reviews-filters">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Source</span>
          <Chip active={source === "all"} onClick={() => setSource("all")} testId="source-all">All</Chip>
          <Chip active={source === "google"} onClick={() => setSource("google")} testId="source-google">
            <Globe2 className="mr-1 inline h-3 w-3" /> Google
          </Chip>
          <Chip active={source === "internal"} onClick={() => setSource("internal")} testId="source-internal">
            <MessageSquare className="mr-1 inline h-3 w-3" /> In-app
          </Chip>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Stars</span>
          <Chip active={star == null} onClick={() => setStar(null)} testId="star-any">Any</Chip>
          {[5, 4, 3, 2, 1].map((n) => (
            <Chip
              key={n}
              active={star === n}
              onClick={() => setStar(star === n ? null : n)}
              testId={`star-${n}`}
              tone={n <= 2 ? "red" : n === 3 ? "amber" : "emerald"}
            >
              {Array.from({ length: n }).map((_, i) => <Star key={i} className="inline h-3 w-3 fill-current" />)}
              {data?.stars_histogram && <span className="ml-1 text-[10px] opacity-70">({data.stars_histogram[n]})</span>}
            </Chip>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-1">
            <ArrowUpDown className="h-3 w-3" /> Sort
          </span>
          <Chip active={sort === "newest"} onClick={() => setSort("newest")} testId="sort-newest">Newest</Chip>
          <Chip active={sort === "oldest"} onClick={() => setSort("oldest")} testId="sort-oldest">Oldest</Chip>
          <Chip active={sort === "lowest"} onClick={() => setSort("lowest")} testId="sort-lowest">Lowest</Chip>
          <Chip active={sort === "highest"} onClick={() => setSort("highest")} testId="sort-highest">Highest</Chip>
        </div>
      </div>

      {/* Review list */}
      {loading && !data && (
        <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-400">Loading reviews…</div>
      )}
      {!loading && data && (data.items.length === 0) && (
        <EmptyState icon={MessageSquare} title="No reviews match these filters" hint="Try clearing a filter or syncing Google benchmarks on the Locations page." />
      )}
      {data && data.items.length > 0 && (
        <div className="space-y-3" data-testid="reviews-list">
          {data.items.map((r, i) => (
            <ReviewCard key={`${r.source}-${r.feedback_id || i}`} r={r} />
          ))}
        </div>
      )}

      {source !== "internal" && (
        <p className="text-center text-[11px] text-slate-400">
          Google's Places API caps us at 5 reviews per store. Link more locations to pool more Google feedback here.
        </p>
      )}
    </div>
  );
}

function SummaryStrip({ data, loading }) {
  if (loading && !data) return null;
  if (!data) return null;
  const g = data.source_counts.google;
  const i = data.source_counts.internal;
  const total = data.total;
  const bar = (n) => (total ? (data.stars_histogram[n] / total) * 100 : 0);
  return (
    <div className="grid gap-3 sm:grid-cols-3" data-testid="reviews-summary">
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Total shown</p>
        <p className="mt-1 font-head text-3xl font-bold text-slate-900">{total.toLocaleString()}</p>
        <p className="text-[11px] text-slate-500">
          <Globe2 className="mr-1 inline h-3 w-3 text-amber-500" />{g} Google ·
          <MessageSquare className="mx-1 inline h-3 w-3 text-indigo-500" />{i} in-app
        </p>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-4 sm:col-span-2">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Star distribution</p>
        <div className="mt-2 space-y-1">
          {[5, 4, 3, 2, 1].map((n) => (
            <div key={n} className="flex items-center gap-2 text-[11px]">
              <span className="w-8 text-right font-semibold text-slate-600">{n}★</span>
              <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
                <div
                  className={`h-full rounded-full ${n <= 2 ? "bg-red-400" : n === 3 ? "bg-amber-400" : "bg-emerald-400"}`}
                  style={{ width: `${bar(n)}%` }}
                />
              </div>
              <span className="w-12 text-right text-slate-500">{data.stars_histogram[n]}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Chip({ active, onClick, children, testId, tone }) {
  const inactive = tone === "red" ? "text-red-700 bg-red-50 hover:bg-red-100"
    : tone === "amber" ? "text-amber-700 bg-amber-50 hover:bg-amber-100"
    : tone === "emerald" ? "text-emerald-700 bg-emerald-50 hover:bg-emerald-100"
    : "text-slate-600 bg-slate-100 hover:bg-slate-200";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold transition ${active ? "bg-indigo-600 text-white hover:bg-indigo-700" : inactive}`}
    >
      {children}
    </button>
  );
}

function ReviewCard({ r }) {
  const rating = r.rating;
  const tone = rating == null ? "slate" : rating <= 2 ? "red" : rating === 3 ? "amber" : "emerald";
  const SentimentIcon = rating == null ? Meh : rating <= 2 ? Frown : rating === 3 ? Meh : Smile;
  const toneClasses = {
    slate: { pill: "bg-slate-50 text-slate-600", icon: "text-slate-400" },
    red: { pill: "bg-red-50 text-red-700", icon: "text-red-400" },
    amber: { pill: "bg-amber-50 text-amber-700", icon: "text-amber-400" },
    emerald: { pill: "bg-emerald-50 text-emerald-700", icon: "text-emerald-400" },
  }[tone];
  return (
    <div
      className="rounded-xl border border-slate-200 bg-white p-4 transition hover:border-indigo-200 hover:shadow-sm"
      data-testid={`review-${r.source}-${r.feedback_id || r.author_name}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          {r.author_photo ? (
            <img src={r.author_photo} alt="" referrerPolicy="no-referrer"
                 className="h-8 w-8 flex-shrink-0 rounded-full border border-slate-200 object-cover" />
          ) : (
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-slate-100 text-[12px] font-bold text-slate-500">
              {(r.author_name || "?").slice(0, 1).toUpperCase()}
            </div>
          )}
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-slate-800">
              {r.author_uri ? (
                <a href={r.author_uri} target="_blank" rel="noreferrer" className="hover:text-indigo-600 hover:underline">
                  {r.author_name}
                </a>
              ) : r.author_name}
            </p>
            <p className="text-[11px] text-slate-500">
              {r.source === "google" ? (
                <>
                  <Globe2 className="mr-1 inline h-2.5 w-2.5 text-amber-500" />Google · {r.relative_time || "—"}
                </>
              ) : (
                <>
                  <MessageSquare className="mr-1 inline h-2.5 w-2.5 text-indigo-500" />In-app · {r.relative_time || "—"}
                </>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1 rounded-full ${toneClasses.pill} px-2 py-0.5 text-xs font-bold`}>
            <Star className="h-3 w-3 fill-current" />
            {rating != null ? rating.toFixed(0) : "—"}
          </span>
          <SentimentIcon className={`h-4 w-4 ${toneClasses.icon}`} />
        </div>
      </div>

      {r.text && (
        <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{r.text}</p>
      )}

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-2 text-[11px]">
        <span className="inline-flex items-center gap-1 text-slate-500">
          <MapPin className="h-3 w-3" />
          <Link to={`/app/store?location=${r.location_id}`} className="font-semibold text-slate-700 hover:text-indigo-600 hover:underline">
            {r.store_name || "—"}
          </Link>
          {r.store_city && <span className="text-slate-400">· {r.store_city}</span>}
        </span>
        {r.source === "google" && r.google_place_name && (
          <span className="inline-flex items-center gap-1 text-[10px] text-slate-400">
            matched to {r.google_place_name} <ExternalLink className="h-2.5 w-2.5" />
          </span>
        )}
        {r.touchpoints && Object.keys(r.touchpoints).length > 0 && (
          <span className="inline-flex items-center gap-1 text-slate-400">
            touchpoints: {Object.keys(r.touchpoints).join(", ")}
          </span>
        )}
      </div>
    </div>
  );
}
